import type { ModuleDef } from './types.js';

/** Insights and pay reporting: reports, dashboards, payroll set-up, ERP hand-off. */
export const INSIGHTS_MODULES: readonly ModuleDef[] = [
  {
    key: 'reports',
    label: 'Reports',
    group: 'insights',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'Open and export the attendance and workforce reports' },
    ],
  },
  {
    key: 'reports.labourCost',
    label: 'Labour cost by site',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'See what own-payroll labour costs at each site' },
    ],
  },
  {
    key: 'reports.suppliers',
    label: 'Agency labour cost',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See hours and billed amounts for agency labour' },
    ],
  },
  {
    key: 'reports.legalEntities',
    label: 'Legal-entity report',
    group: 'insights',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'See headcount and hours split by legal entity' },
    ],
  },
  {
    key: 'reports.gratuity',
    label: 'Gratuity report',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See an employee\'s end-of-service gratuity calculation' },
    ],
  },
  {
    key: 'reports.wps',
    label: 'WPS wage file',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'Generate the wage-protection (WPS) file for a period' },
    ],
  },
  {
    key: 'reports.payslip',
    label: 'Payslip preview',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'Preview payslips for a payroll period' },
    ],
  },
  {
    key: 'pay.periods',
    label: 'Pay periods',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See which months are closed and the changes waiting on a closed month' },
      { action: 'manage', defaultRoles: ['HR'], description: 'Close a month so its figures stop changing, or reopen it with a reason' },
      { action: 'approve', defaultRoles: ['HR'], description: 'Accept or reject a change to a closed month' },
    ],
  },
  {
    key: 'dashboard.executive',
    label: 'Executive overview',
    group: 'insights',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See the company-wide executive KPI page' },
    ],
  },
  {
    key: 'dashboard.movements',
    label: 'Movement feed',
    group: 'insights',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'See named worker arrivals and departures as they happen' },
    ],
  },
  {
    key: 'pay.allowances',
    label: 'Allowances',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See the list of pay allowances' },
      { action: 'create', defaultRoles: ['HR'], description: 'Add a new allowance' },
    ],
  },
  {
    key: 'pay.deductions',
    label: 'Deductions',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See the list of pay deductions' },
      { action: 'create', defaultRoles: ['HR'], description: 'Add a new deduction' },
    ],
  },
  {
    key: 'pay.loans',
    label: 'Employee loans',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See employee loans and their balances' },
      { action: 'create', defaultRoles: ['HR'], description: 'Record a new employee loan' },
      { action: 'edit', defaultRoles: ['HR'], description: 'Record a repayment against a loan' },
    ],
  },
  {
    key: 'erp',
    label: 'ERP integration',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR'], description: 'See the ERP connection, batches and inbound changes' },
      { action: 'manage', defaultRoles: [], platform: true, description: 'Change the ERP connection and run a sync' },
      { action: 'approve', defaultRoles: ['HR'], description: 'Approve or reject ERP batches and inbound changes' },
    ],
  },
  {
    key: 'erp.clockcards',
    label: 'ERP clock-card export',
    group: 'pay',
    permissions: [
      { action: 'export', defaultRoles: ['HR'], description: 'Download the monthly clock-card file for the ERP' },
    ],
  },
  {
    key: 'erp.ledger',
    label: 'ERP ledger accounts',
    group: 'pay',
    permissions: [
      { action: 'view', defaultRoles: ['HR', 'MANAGER'], description: 'Pick from the ERP chart of accounts' },
    ],
  },
];
